import React from 'react';
import { Icon } from '@iconify/react';
import { MultiSelectPopover, type MultiselectOption } from './MultiSelectPopover';
import { SegmentedControl } from './SegmentedControl';

export type GroupByKey = 'campaign' | 'week' | 'day' | 'channel' | 'substatus';

export interface FilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  groupOptions: MultiselectOption[];
  groupIds: Set<string>;
  onGroupIdsChange: (next: Set<string>) => void;
  groupLabel: string;
  groupIcon: string;
  statusOptions: MultiselectOption[];
  statuses: Set<string>;
  onStatusesChange: (next: Set<string>) => void;
  channelOptions: MultiselectOption[];
  channels: Set<string>;
  onChannelsChange: (next: Set<string>) => void;
  /** Active filter count for the "Clear N" reset chip. */
  activeCount: number;
  onReset: () => void;
  /** Group-by dimension (Campaign / Week / Channel). When provided, a switch is shown. */
  groupBy?: GroupByKey;
  onGroupByChange?: (next: GroupByKey) => void;
  groupByOptions?: GroupByKey[];
  groupByControlLabel?: string;
  groupByCampaignLabel?: string;
  /** Place the grouping control before scoping filters and search. */
  groupByFirst?: boolean;
  trailingControls?: React.ReactNode;
  /**
   * Hide the campaign/group multi-select. Used by the Kanban view, where the
   * Lead Pool column is the campaign selector — a second campaign filter on top
   * would be redundant.
   */
  hideGroupFilter?: boolean;
  /** Hide status scoping when the board itself already exposes every stage. */
  hideStatusFilter?: boolean;
}

/**
 * FilterBar — compact horizontal filter strip for full-bleed views (Calendar,
 * Kanban). Campaigns/accounts, status, and channels are multi-select filters;
 * an optional group-by switch re-buckets the board by Campaign, Week, or
 * Channel.
 */
export const FilterBar: React.FC<FilterBarProps> = ({
  searchTerm,
  onSearchChange,
  groupOptions,
  groupIds,
  onGroupIdsChange,
  groupLabel,
  groupIcon,
  statusOptions,
  statuses,
  onStatusesChange,
  channelOptions,
  channels,
  onChannelsChange,
  activeCount,
  onReset,
  groupBy,
  onGroupByChange,
  groupByOptions = ['campaign', 'week', 'day', 'channel'],
  groupByControlLabel = 'Group by',
  groupByCampaignLabel = groupLabel,
  groupByFirst = false,
  trailingControls,
  hideGroupFilter = false,
  hideStatusFilter = false,
}) => {
  const groupByOptionMeta: Record<GroupByKey, { icon: string; label: string }> = {
    campaign: { icon: groupIcon, label: groupByCampaignLabel },
    week: { icon: 'bx:calendar-week', label: 'Week' },
    day: { icon: 'bx:calendar', label: 'Day' },
    channel: { icon: 'bx:grid-alt', label: 'Channel' },
    substatus: { icon: 'bx:git-branch', label: 'Substatus' },
  };
  const groupByControl = groupBy && onGroupByChange ? (
    <div className="inline-flex min-h-9 flex-wrap items-center gap-1 rounded-xl bg-white/[0.018] p-0.5 pl-2">
      <span className="inline-flex items-center gap-1.5 pr-1 text-[9px] font-black uppercase tracking-[0.12em] text-white/35">
        <Icon icon="bx:layer" className="h-3.5 w-3.5" />
        {groupByControlLabel}
      </span>
      <SegmentedControl
        ariaLabel={groupByControlLabel}
        size="sm"
        tone="neutral"
        value={groupBy}
        onChange={(value) => onGroupByChange(value as GroupByKey)}
        className="border-transparent bg-transparent shadow-none"
        options={groupByOptions.map((value) => ({
          value,
          icon: groupByOptionMeta[value].icon,
          label: groupByOptionMeta[value].label,
        }))}
      />
    </div>
  ) : null;

  return (
    <div className="unibox-filterbar mb-2 flex flex-wrap items-center gap-1.5 rounded-xl bg-transparent p-0.5" data-view-block>
      {groupByFirst && groupByControl}

      {/* Scoping filters stay left; the search box grows into remaining space. */}
      {!hideStatusFilter && (
        <MultiSelectPopover placeholder="Status" icon="bx:tag" options={statusOptions} selected={statuses} onChange={onStatusesChange} />
      )}
      <MultiSelectPopover placeholder="Channels" icon="bx:grid-alt" options={channelOptions} selected={channels} onChange={onChannelsChange} />
      {!hideGroupFilter && (
        <MultiSelectPopover placeholder={groupLabel} icon={groupIcon} options={groupOptions} selected={groupIds} onChange={onGroupIdsChange} />
      )}

      <label className="relative block min-w-[160px] flex-1">
        <Icon icon="bx:search" className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/32" />
        <input
          value={searchTerm}
          onChange={(event) => onSearchChange(event.target.value)}
          name="unibox-search"
          autoComplete="off"
          aria-label="Search pipeline"
          placeholder="Search…"
          className="h-9 w-full rounded-lg border border-transparent bg-white/[0.025] pl-8 pr-3 text-xs font-semibold text-white outline-none transition-colors placeholder:text-[#7D879B] hover:bg-white/[0.04] focus:bg-black/[0.20] focus-visible:ring-1 focus-visible:ring-cyan-300/[0.24]"
        />
      </label>

      {!groupByFirst && groupByControl}

      {trailingControls}

      {activeCount > 0 && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[10px] font-bold text-white/45 transition-colors hover:bg-white/[0.045] hover:text-white/75 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-white/[0.18]"
        >
          <Icon icon="bx:reset" className="h-3.5 w-3.5" />
          Clear {activeCount}
        </button>
      )}
    </div>
  );
};

export default FilterBar;
